import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const StudentResults = () => {
  const { user } = useAuth();
  const [resultsData, setResultsData] = useState([]);

  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchResultsData = async () => {
      try {
        // Fetch student profile first
        const profileResponse = await api.get(`/students/user/${user?._id}`);
        
        // Fetch student results
        const resultsResponse = await api.get(`/results/student/${profileResponse.data.data._id}`);
        
        setResultsData(resultsResponse.data.data);
      } catch (error) {
        console.error('Error fetching results data:', error);
        // Fallback to mock data if API fails
        const mockResults = [
          {
            id: 1,
            exam: 'Mid Term Exam',
            class: 'Grade 10',
            subject: 'Mathematics',
            marks: 85,
            totalMarks: 100,
            percentage: 85,
            grade: 'A',
            date: '2023-11-15'
          },
          {
            id: 2,
            exam: 'Mid Term Exam',
            class: 'Grade 10',
            subject: 'Science',
            marks: 92,
            totalMarks: 100,
            percentage: 92,
            grade: 'A+',
            date: '2023-11-16'
          },
          {
            id: 3,
            exam: 'Unit Test',
            class: 'Grade 10',
            subject: 'English',
            marks: 78,
            totalMarks: 100,
            percentage: 78,
            grade: 'B+',
            date: '2023-10-20'
          },
          {
            id: 4,
            exam: 'Quiz',
            class: 'Grade 10',
            subject: 'History',
            marks: 45,
            totalMarks: 50,
            percentage: 90,
            grade: 'A',
            date: '2023-10-25'
          },
          {
            id: 5,
            exam: 'Semester Final',
            class: 'Grade 9',
            subject: 'Mathematics',
            marks: 76,
            totalMarks: 100,
            percentage: 76,
            grade: 'B',
            date: '2023-08-15'
          }
        ];
        
        setResultsData(mockResults);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchResultsData();
    }
  }, [user]);

  // Group results by exam type
  const groupedResults = {};
  resultsData.forEach(result => {
    if (!groupedResults[result.exam]) {
      groupedResults[result.exam] = [];
    }
    groupedResults[result.exam].push(result);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">My Results</h1>
        
        {/* Results Summary */}
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-wrap justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-semibold text-gray-800">Result Summary</h2>
              <p className="text-gray-600">For {user?.name || 'Student'}</p>
            </div>
            
            <div className="flex flex-wrap gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">{resultsData.length}</p>
                <p className="text-gray-600">Exams Taken</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">
                  {resultsData.length > 0 ? (resultsData.reduce((sum, r) => sum + r.percentage, 0) / resultsData.length).toFixed(1) : 0}%
                </p>
                <p className="text-gray-600">Avg. Percentage</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-800">
                  {resultsData.filter(r => r.grade === 'A+' || r.grade === 'A').length}
                </p>
                <p className="text-gray-600">A Grades</p>
              </div>
            </div>
          </div>
        </div>

        {/* Exam-wise Results */}
        <div className="max-w-4xl mx-auto">
          {Object.entries(groupedResults).map(([exam, results]) => (
            <div key={exam} className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">{exam}</h2>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results.map((result, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{result.subject}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.class}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.marks}/{result.totalMarks}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.percentage}%</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            result.grade === 'A+' ? 'bg-purple-100 text-purple-800' :
                            result.grade === 'A' ? 'bg-green-100 text-green-800' :
                            result.grade === 'B+' ? 'bg-yellow-100 text-yellow-800' :
                            result.grade === 'B' ? 'bg-orange-100 text-orange-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {result.grade}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{result.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StudentResults;